import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../firebase';

// Validation de la complexité du mot de passe
const validatePassword = (password: string): boolean => {
  // Minimum 8 caractères, au moins une lettre, un chiffre et un caractère spécial
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
  return passwordRegex.test(password);
};

export function NewPassword() {
  const { t } = useTranslation();
  // Utiliser oobCode plutôt que token pour la compatibilité Firebase
  const { token: oobCode } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'validating' | 'valid' | 'invalid' | 'submitting' | 'success'>('validating');
  const [error, setError] = useState('');
  // Cette variable est utilisée pour afficher à quel email le code de réinitialisation appartient
  const [userEmail, setUserEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | ''>('');

  useEffect(() => {
    validateToken();
  }, []);

  useEffect(() => {
    // Évaluer la force du mot de passe
    if (!password) {
      setPasswordStrength('');
    } else if (password.length < 8) {
      setPasswordStrength('weak');
    } else if (validatePassword(password)) {
      setPasswordStrength('strong');
    } else {
      setPasswordStrength('medium');
    }
  }, [password]);

  const validateToken = async () => {
    if (!oobCode) {
      setStatus('invalid');
      setError(t('reset_password.error.missing_code'));
      return;
    }

    try {
      // Utiliser la fonction Firebase pour vérifier le code
      const email = await authService.verifyPasswordResetCode(oobCode);
      setUserEmail(email); // Stocker l'email dans userEmail au lieu de email
      setStatus('valid');
    } catch (err) {
      setStatus('invalid');
      setError(t('reset_password.error.invalid_token'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword(password)) {
      setError(t('reset_password.error.password_complexity'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('reset_password.error.passwords_dont_match'));
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      // Utiliser la fonction Firebase pour confirmer la réinitialisation du mot de passe
      await authService.confirmResetPassword(oobCode || '', password);
      setStatus('success');
      
      // Rediriger vers la page de connexion après un délai
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setStatus('valid');
      
      // Gestion des erreurs Firebase
      if (err.code === 'auth/expired-action-code') {
        setError(t('reset_password.error.expired_code'));
      } else if (err.code === 'auth/invalid-action-code') {
        setError(t('reset_password.error.invalid_code'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('reset_password.error.weak_password'));
      } else {
        setError(t('reset_password.error.reset_failed'));
      }
    }
  };

  if (status === 'validating') {
    return <div className="text-center">{t('reset_password.validating')}</div>;
  }

  if (status === 'invalid') {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-md w-full space-y-8 bg-white bg-opacity-90 dark:bg-black dark:bg-opacity-90 border border-gray-200 dark:border-gray-700 p-8 rounded-2xl shadow-2xl backdrop-blur-lg">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('reset_password.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('reset_password.subtitle')}
        </p>
        {/* Afficher l'email si disponible */}
        {userEmail && (
          <p className="mt-2 text-blue-600 dark:text-blue-400">
            {t('reset_password.for_email', { email: userEmail })}
          </p>
        )}
      </div>

      {status === 'success' ? (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-900 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg">
          {t('reset_password.success')}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('reset_password.password.label')}
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('reset_password.password.placeholder')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('reset_password.confirm_password.label')}
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('reset_password.confirm_password.placeholder')}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {status === 'submitting' ? t('reset_password.submitting') : t('reset_password.submit')}
          </button>
        </form>
      )}

      {/* Add password strength indicator */}
      {passwordStrength && (
        <div className="mt-2">
          <div className="text-sm font-medium mb-1">
            {t(`reset_password.password_strength.${passwordStrength}`)}
          </div>
          <div className="h-2 rounded-full bg-gray-200">
            <div 
              className={`h-full rounded-full ${
                passwordStrength === 'weak' ? 'bg-red-500 w-1/3' : 
                passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' : 
                'bg-green-500 w-full'
              }`}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
}
